import { useCallback, useEffect } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useVenue } from "providers/venue"

import { PARKING_FINDER_QUERY_KEY } from "./constants"
import type { ParkingFinderRecord } from "./types"
import { remove as removePersistValue } from "utils/persist_store"
import { KIOSK_CONFIG_NAME_KEY } from "constant"
import { KIOSK_CONFIG_QUERY_KEY } from "providers/venue/modules/data/kiosk-config/constants"

export const useParkingFinder = () => {
  const queryClient = useQueryClient()
  const { findSectionByReferenceId } = useVenue()

  const { data: cachedRecord = null } = useQuery<ParkingFinderRecord | null>({
    queryKey: PARKING_FINDER_QUERY_KEY,
    queryFn: () => {
      const cached = queryClient.getQueryData<ParkingFinderRecord | null>(
        PARKING_FINDER_QUERY_KEY
      )
      if (!cached || cached.expiresAt <= Date.now()) return null
      return cached
    },
    staleTime: 1000 * 60,
    gcTime: Infinity,
    meta: { persist: true },
  })

  const clear = useCallback(() => {
    queryClient.setQueryData(PARKING_FINDER_QUERY_KEY, null)
    queryClient.removeQueries({
      queryKey: KIOSK_CONFIG_QUERY_KEY,
    })
    removePersistValue(KIOSK_CONFIG_NAME_KEY)
  }, [queryClient])

  // Check expiry every time we read the record.
  // The queryFn (which also checks expiry) does not run again on an idle kiosk,
  // and save() writes to the cache directly without running it. So an expired
  // record can stay in the cache. If it is past expiresAt, treat it as empty.
  const savedRecord =
    cachedRecord && cachedRecord.expiresAt > Date.now() ? cachedRecord : null

  // On mount, a record restored from storage may already be past expiresAt.
  // The read-time check above hides it; this also purges it from the cache and
  // storage so stale data does not pile up. Sessions are short (mobile use), so
  // we don't schedule a timer for the expiry moment — it's evaluated on open.
  useEffect(() => {
    if (cachedRecord && cachedRecord.expiresAt <= Date.now()) {
      clear()
    }
  }, [cachedRecord, clear])

  const feature = savedRecord
    ? findSectionByReferenceId(savedRecord.referenceId) ?? null
    : null

  const save = useCallback(
    (referenceId: string, note: string, expiresAt: number) => {
      const record: ParkingFinderRecord = {
        referenceId,
        note,
        savedAt: Date.now(),
        expiresAt,
        shouldOpenAfterSave: true,
      }
      queryClient.setQueryData(PARKING_FINDER_QUERY_KEY, record)
    },
    [queryClient]
  )

  return {
    savedRecord,
    feature,
    isActive: savedRecord !== null,
    save,
    clear,
  }
}
